import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, MapPin, Briefcase, GraduationCap, Play, ChevronDown, ChevronUp, Award, Code, CheckCircle, Heart, FileText, ChevronLeft, ChevronRight, Globe, Ban, RotateCcw, Sparkles, ExternalLink, Trophy, Eye, Info, ShieldCheck, Clock } from 'lucide-react';
import { getDeveloperDetails, DeveloperDetails, viewTalent, saveTalent, unsaveTalent, discardTalent, restoreTalent, getRecordingUrl, getAiInterviewStreamUrl, DomainType } from '../services/api';
import { SoftwareTalent } from '../types';
import { filterContractOnlyCompanies } from '../utils/credibilityLine';
import { prepareHighlights } from '../utils/highlights';
import { getDomainConfig, isAcademicDomain } from '../config/appConfig';
import { TimelineCompanyLogo, UniversityLogo, CompanyLogo, LinkedInLogo, GoogleScholarLogo, BlueTick } from './BrandLogos';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { trackEvent } from '../utils/mixpanel';
import { getCached, setCache } from '../utils/apiCache';


/** Format large numbers for display (e.g. 1200 → "1.2k") */
function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return n.toString();
}

/** Skills shown before "+N more" — matches search result cards. */
const MAX_SKILLS_DISPLAY = 5;

function formatRelativeTimeVerbose(timestamp: string | null | undefined): string {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'just now';
  const days = Math.floor(diffMs / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return '1 week ago';
  if (weeks < 5) return `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

// Module-level set to prevent duplicate view API calls from React StrictMode
const pendingViewCalls = new Set<number>();

// Module-level map to prevent duplicate fetch calls from React StrictMode
const pendingFetchCalls = new Map<string, Promise<DeveloperDetails>>();

interface DeveloperProfileProps {
  talentId: number;
  domain: DomainType;
  onBack: () => void;
  searchTalent?: SoftwareTalent;
  allTalent?: SoftwareTalent[];
  onNavigateTalent?: (talent: SoftwareTalent) => void;
  onView?: (talentId: number, domain?: DomainType) => void;
  onSave?: (talentId: number, domain?: DomainType) => void;
  onUnsave?: (talentId: number, domain?: DomainType) => void;
  onDiscard?: (talentId: number, domain?: DomainType) => void;
  onRestore?: (talentId: number, domain?: DomainType) => void;
  isSaved?: boolean;
  isDiscarded?: boolean;
  discardedIds?: Set<number>;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
  loadingMore?: boolean;
  activeTab?: 'search' | 'saved' | 'viewed' | 'discarded';
  hideHeader?: boolean;
  collapsibleSections?: boolean;
  skipViewTracking?: boolean;
  /** Admin flows: fetch details without sending tid so cross-tenant profiles resolve. */
  bypassTenant?: boolean;
  /** Called once after details are fetched with initial action state. */
  onDetailsLoaded?: (isSaved: boolean, isDiscarded: boolean) => void;
}

/** Return up to 2 highlight schools: prefer bayAreaTalentSignals.evidence.topSchools, fallback to pedigreeSignals.topSchool, then education[0].school */
function getHighlightSchools(dev: DeveloperDetails): { name: string; abbrev: string }[] {
  const fromEvidence = (dev.bayAreaTalentSignals?.evidence?.topSchools ?? [])
    .filter(s => s?.name?.trim())
    .map(s => ({ name: s.name!.trim(), abbrev: s.abbrev?.trim() || s.name!.trim() }));
  if (fromEvidence.length > 0) return fromEvidence.slice(0, 2);
  const fromPedigree = dev.pedigreeSignals?.topSchool?.trim();
  if (fromPedigree) return [{ name: fromPedigree, abbrev: fromPedigree }];
  const fromEdu = dev.education?.[0]?.school?.trim();
  if (fromEdu) return [{ name: fromEdu, abbrev: fromEdu }];
  return [];
}

/* ------------------------------------------------------------------ */
/*  InlineSection – collapsible section toggle inside the unified card */
/* ------------------------------------------------------------------ */
function InlineSection({
  title,
  icon,
  children,
  isOpen,
  onToggle,
  isLast = false,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  isLast?: boolean;
}) {
  return (
    <div className={!isLast ? 'border-b border-slate-100 dark:border-slate-800' : ''}>
      <button
        onClick={onToggle}
        className="w-full py-5 px-5 flex items-start justify-between text-left cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
          </div>
        </div>
        <div className="flex-shrink-0 ml-4 mt-0.5">
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-slate-400 dark:text-slate-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400 dark:text-slate-500" />
          )}
        </div>
      </button>
      {isOpen && <div className="pt-0 pb-5 px-5">{children}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  WorkExperienceTimeline                                             */
/* ------------------------------------------------------------------ */
function WorkExperienceTimeline({
  workExperience,
}: {
  workExperience: NonNullable<DeveloperDetails['workExperience']>;
}) {
  const [expandedJobs, setExpandedJobs] = useState<Set<number>>(
    () => new Set(workExperience.map((_, i) => i)) // all expanded by default
  );
  const [showAll, setShowAll] = useState(false);
  const visibleJobs = showAll ? workExperience : workExperience.slice(0, 3);
  const hasMore = workExperience.length > 3;

  const toggleJob = (idx: number) => {
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-4 top-4 bottom-4 w-px bg-slate-200 dark:bg-slate-700" />

      <div className="space-y-0">
        {visibleJobs.map((job, index) => {
          const isExpanded = expandedJobs.has(index);
          const isLast = index === visibleJobs.length - 1 && !hasMore;
          const duration = (() => {
            if (job.startYear || job.endYear) {
              return `${job.startYear || '?'} - ${job.endYear ? `${job.endYear}` : 'Present'}`;
            }
            return null;
          })();
          return (
            <div key={index} className="relative">
              <div className="absolute left-0">
                <TimelineCompanyLogo company={job.company || 'Unknown'} />
              </div>
              <div className={`ml-12 ${!isLast ? 'pb-4' : ''}`}>
                <button onClick={() => toggleJob(index)} className="w-full text-left group">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {job.position && job.position !== 'Unknown Title' ? (
                        <>
                          <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-sm leading-tight">
                            {job.position}
                          </h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                            {job.company || 'Unknown Company'}
                          </p>
                        </>
                      ) : (
                        <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-sm leading-tight">
                          {job.company || 'Unknown Company'}
                        </h4>
                      )}
                      {duration && (
                        <p className="text-sm text-slate-500 dark:text-slate-500">{duration}</p>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0 mt-1" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0 mt-1 group-hover:text-slate-600 dark:group-hover:text-slate-400" />
                    )}
                  </div>
                </button>

                {isExpanded && job.description && Array.isArray(job.description) && job.description.length > 0 && (
                  <ul className="mt-3 space-y-1 pl-0 ml-0">
                    {job.description.map((bullet, bulletIndex) => (
                      <li key={bulletIndex} className="flex items-start gap-3 py-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600 mt-[7px] flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {hasMore && (
        <div className="ml-8 mt-2 pt-2">
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
          >
            {showAll ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show {workExperience.length - 3} more position{workExperience.length - 3 > 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

const DeveloperProfile: React.FC<DeveloperProfileProps> = ({
  talentId,
  domain,
  onBack,
  searchTalent,
  allTalent = [],
  onNavigateTalent,
  onView,
  onSave,
  onUnsave,
  onDiscard,
  onRestore,
  isSaved: initialIsSaved = false,
  isDiscarded: initialIsDiscarded = false,
  discardedIds = new Set(),
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  activeTab = 'search',
  hideHeader = false,
  collapsibleSections = false,
  skipViewTracking = false,
  bypassTenant = false,
  onDetailsLoaded,
}) => {
  const [developer, setDeveloper] = useState<DeveloperDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMoreSkills, setShowMoreSkills] = useState(false);
  const [isSaved, setIsSaved] = useState(initialIsSaved);
  const [isDiscarded, setIsDiscarded] = useState(initialIsDiscarded);
  const [viewedAt, setViewedAt] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<'save' | 'unsave' | 'discard' | 'restore' | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  
  // Coding challenge recording state
  const [codingChallengeRecordingUrl, setCodingChallengeRecordingUrl] = useState<string | null>(null);
  const [codingChallengeRecordingLoading, setCodingChallengeRecordingLoading] = useState(false);
  const [codingChallengeRecordingError, setCodingChallengeRecordingError] = useState<string | null>(null);

  // Section expansion state (for collapsible mode)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['publications', 'vetting', 'experience', 'education', 'certifications', 'projects', 'languages', 'hobbies'])
  );
  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };
  
  // Track the previous allTalent length to detect when new talent is loaded
  const prevTalentLengthRef = React.useRef(allTalent.length);

  // Find current position in talent list for prev/next navigation
  const currentIndex = allTalent.findIndex(t => t.id === talentId);
  const prevTalent = currentIndex > 0 ? allTalent[currentIndex - 1] : null;
  const nextTalentInList = currentIndex < allTalent.length - 1 ? allTalent[currentIndex + 1] : null;
  const atEndOfLoadedList = nextTalentInList === null && hasMore;
  const nextTalent = nextTalentInList;
  const hasNext = nextTalent !== null || atEndOfLoadedList;
  
  // Auto-navigate to next talent when new talent is loaded after clicking Next at end
  useEffect(() => {
    if (pendingNavigation && allTalent.length > prevTalentLengthRef.current) {
      const nextTalent = allTalent[currentIndex + 1];
      if (nextTalent) {
        onNavigateTalent?.(nextTalent);
        setPendingNavigation(false);
      }
    }
    prevTalentLengthRef.current = allTalent.length;
  }, [allTalent.length, pendingNavigation, currentIndex, onNavigateTalent, allTalent]);

  // Fetch developer details (stale-while-revalidate: show cached data instantly, refresh in background)
  const fetchIdRef = React.useRef(0);

  useEffect(() => {
    const thisFetchId = ++fetchIdRef.current;
    const fetchKey = `${talentId}-${domain}`;
    const cacheKey = `dev-${talentId}-${domain}`;
    const cached = getCached<DeveloperDetails>(cacheKey);

    setError(null);
    setShowMoreSkills(false);
    setCodingChallengeRecordingUrl(null);
    setCodingChallengeRecordingLoading(false);
    setCodingChallengeRecordingError(null);

    if (cached) {
      setDeveloper(cached);
      if (cached.isSaved !== undefined) setIsSaved(cached.isSaved);
      if (cached.isDiscarded !== undefined) setIsDiscarded(cached.isDiscarded);
      if (cached.viewedAt) setViewedAt(cached.viewedAt);
      setLoading(false);
      onDetailsLoaded?.(cached.isSaved ?? false, cached.isDiscarded ?? false);
    } else {
      setDeveloper(null);
      setLoading(true);
    }

    let fetchPromise = pendingFetchCalls.get(fetchKey);
    if (!fetchPromise) {
      fetchPromise = getDeveloperDetails(String(talentId), domain, { bypassTenant });
      pendingFetchCalls.set(fetchKey, fetchPromise);
      fetchPromise.finally(() => {
        pendingFetchCalls.delete(fetchKey);
      });
    }

    fetchPromise
      .then((data) => {
        if (fetchIdRef.current === thisFetchId) {
          setCache(cacheKey, data);
          setDeveloper(data);
          if (data.isSaved !== undefined) setIsSaved(data.isSaved);
          if (data.isDiscarded !== undefined) setIsDiscarded(data.isDiscarded);
          if (data.viewedAt) setViewedAt(data.viewedAt);
          setLoading(false);
          onDetailsLoaded?.(data.isSaved ?? false, data.isDiscarded ?? false);
        }
      })
      .catch((err) => {
        if (fetchIdRef.current === thisFetchId) {
          if (!cached) {
            setError(err?.message || 'Failed to load talent details');
          }
          setLoading(false);
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId, domain]);

  // Fetch coding challenge recording URL
  const fetchCodingChallengeRecording = useCallback(async (submitId: number) => {
    if (codingChallengeRecordingUrl || codingChallengeRecordingLoading) return;
    setCodingChallengeRecordingLoading(true);
    setCodingChallengeRecordingError(null);
    try {
      const response = await getRecordingUrl(String(talentId), submitId);
      if (response?.recordingURL) {
        setCodingChallengeRecordingUrl(response.recordingURL);
      } else {
        setCodingChallengeRecordingError('Recording not available');
      }
    } catch (err) {
      setCodingChallengeRecordingError('Failed to load recording');
    } finally {
      setCodingChallengeRecordingLoading(false);
    }
  }, [talentId, codingChallengeRecordingUrl, codingChallengeRecordingLoading]);

  // Call view API when developer details are loaded
  useEffect(() => {
    if (skipViewTracking) return;
    if (!talentId || loading || error || !developer) return;
    if (initialIsDiscarded) return;
    if (pendingViewCalls.has(talentId)) return;
    pendingViewCalls.add(talentId);
    viewTalent(talentId, domain)
      .then(() => { onView?.(talentId, domain); })
      .catch(() => {})
      .finally(() => { setTimeout(() => pendingViewCalls.delete(talentId), 100); });
  }, [talentId, loading, error, developer, domain, onView, initialIsDiscarded, skipViewTracking]);

  // Reset viewedAt when navigating to a different profile
  useEffect(() => {
    setViewedAt(null);
  }, [talentId]);

  useEffect(() => { setIsSaved(initialIsSaved); }, [initialIsSaved]);
  useEffect(() => { setIsDiscarded(initialIsDiscarded); }, [initialIsDiscarded]);

  const handleSave = useCallback(async () => {
    if (actionLoading) return;
    setActionLoading('save');
    try {
      if (isDiscarded) {
        await restoreTalent(talentId, domain);
        setIsDiscarded(false);
        onRestore?.(talentId, domain);
      }
      await saveTalent(talentId, domain);
      setIsSaved(true);
      onSave?.(talentId, domain);
    } catch (err) { console.error('Failed to save talent:', err); }
    finally { setActionLoading(null); }
  }, [actionLoading, talentId, domain, onSave, isDiscarded, onRestore]);

  const handleUnsave = useCallback(async () => {
    if (actionLoading) return;
    setActionLoading('unsave');
    try {
      await unsaveTalent(talentId, domain);
      setIsSaved(false);
      onUnsave?.(talentId, domain);
    } catch (err) { console.error('Failed to unsave talent:', err); }
    finally { setActionLoading(null); }
  }, [actionLoading, talentId, domain, onUnsave]);

  const handleDiscard = useCallback(async () => {
    if (actionLoading) return;
    setActionLoading('discard');
    try {
      await discardTalent(talentId, domain);
      setIsDiscarded(true);
      setIsSaved(false);
      onDiscard?.(talentId, domain);
    } catch (err) { console.error('Failed to discard talent:', err); }
    finally { setActionLoading(null); }
  }, [actionLoading, talentId, domain, onDiscard]);

  const handleRestore = useCallback(async () => {
    if (actionLoading) return;
    setActionLoading('restore');
    try {
      await restoreTalent(talentId, domain);
      setIsDiscarded(false);
      onRestore?.(talentId, domain);
    } catch (err) { console.error('Failed to restore talent:', err); }
    finally { setActionLoading(null); }
  }, [actionLoading, talentId, domain, onRestore]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateString; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[70vh]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">Loading talent details...</p>
        </div>
      </div>
    );
  }

  if (error || !developer) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Talent not found</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">{error || 'The talent you are looking for does not exist.'}</p>
          <button onClick={onBack} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            Back to Results
          </button>
        </div>
      </div>
    );
  }

  const codingChallenge = developer.codingChallengeDetails;
  const aiInterview = developer.aiInterviewDetails;
  const hasAiInterviewRecording =
    Boolean(aiInterview?.hasRecording) || Boolean(aiInterview?.recordingURL?.trim());
  const aiInterviewStreamUrl = hasAiInterviewRecording
    ? getAiInterviewStreamUrl(String(talentId), domain)
    : null;
  const hasAiInterviewContent =
    aiInterview &&
    (hasAiInterviewRecording ||
      (Array.isArray(aiInterview.interviewSummary) && aiInterview.interviewSummary.length > 0) ||
      (Array.isArray(aiInterview.transcript) && aiInterview.transcript.length > 0));
  const highlightSchools = getHighlightSchools(developer);
  const workExp = developer.workExperience ?? searchTalent?.workExperience ?? [];
  const notableCompanies = filterContractOnlyCompanies(
    (
      developer.pedigreeSignals?.notableCompanies ??
      developer.bayAreaTalentSignals?.evidence?.notableCompanies ??
      searchTalent?.pedigreeSignals?.notableCompanies ??
      []
    ).map((c) => c?.trim()).filter(Boolean) as string[],
    workExp,
  );

  const hasVettingContent = !!(codingChallenge && codingChallenge.submitId) || hasAiInterviewContent;
  const hasLinkedInVerification = developer.linkedInVerify === 1 || !!(developer.linkedInVerification && (developer.linkedInVerification.Identity?.length || developer.linkedInVerification.Workplace?.length || developer.linkedInVerification.EducationalInstitution?.length || developer.linkedInVerification.joinedDate));
  const hasResearchProfile = !!(developer.scholarVerify?.url || (developer as any).googleScholarURL || (developer.teachingOrResearchProfile?.isVerified && developer.teachingOrResearchProfile?.link));
  const hasAdditionalVerifications = !!(developer.additionalVerifications && developer.additionalVerifications.some(item => {
    const trimmed = item.link?.trim();
    if (!trimmed) return false;
    try { return /^https?:$/.test(new URL(trimmed).protocol); } catch { return false; }
  }));
  const hasVetting = hasVettingContent || hasLinkedInVerification || hasResearchProfile || hasAdditionalVerifications;
  const vettingSectionTitle = hasVettingContent ? 'Vetting & Verification' : 'Verification';
  const hasExperience = developer.workExperience && developer.workExperience.length > 0;
  const hasEducation = developer.education && developer.education.length > 0;
  const hasCertifications = developer.certificates && developer.certificates.length > 0;
  const hasLanguages = developer.languages?.natural && developer.languages.natural.length > 0;
  const hasHobbies = developer.hobbies && developer.hobbies.length > 0;
  const hasProjects = developer.projectDetails && developer.projectDetails.length > 0;

  const publicationsRaw = developer.scholarVerify?.publications?.length
    ? developer.scholarVerify.publications
    : developer.researchPapers?.length
      ? developer.researchPapers.map((rp) => ({
          title: rp.title ?? null,
          authors: Array.isArray(rp.authors) ? rp.authors.join(', ') : (rp.authors ?? null),
          year: typeof rp.year === 'string' ? parseInt(rp.year, 10) || null : (rp.year ?? null),
          citations: null as number | null,
          link: rp.url ?? null,
        }))
      : null;
  // Drop title-less entries — a publication card with no title is useless
  const publications = publicationsRaw?.filter((pub) => pub.title && pub.title.trim()) ?? null;
  const hasPublications = (isAcademicDomain(domain)) && publications && publications.length > 0;

  // Scholar metrics from detail response
  const detailScholarCitations = developer.scholarVerify?.citations;
  const detailScholarHIndex = developer.scholarVerify?.hIndex;
  const detailScholarI10Index = developer.scholarVerify?.i10Index;
  const hasDetailScholarMetrics = detailScholarCitations != null || detailScholarHIndex != null || detailScholarI10Index != null;

  // GitHub metrics from detail response
  const detailGithubData = developer.githubData;
  const ghDetailNonZeroCount = detailGithubData ? [detailGithubData.contributions, detailGithubData.repos, detailGithubData.stars].filter(v => v != null && v > 0).length : 0;
  const hasDetailGithubMetrics = ghDetailNonZeroCount >= 2;

  // Determine which sections exist for the unified card
  const unifiedSections: { id: string; exists: boolean }[] = (isAcademicDomain(domain))
    ? [
        { id: 'vetting', exists: !!hasVetting },
        { id: 'education', exists: !!hasEducation },
        { id: 'experience', exists: !!hasExperience },
        { id: 'publications', exists: !!hasPublications },
        { id: 'certifications', exists: !!hasCertifications },
        { id: 'projects', exists: !!hasProjects },
        { id: 'languages', exists: !!hasLanguages },
        { id: 'hobbies', exists: !!hasHobbies },
      ]
    : [
        { id: 'publications', exists: !!hasPublications },
        { id: 'vetting', exists: !!hasVetting },
        { id: 'experience', exists: !!hasExperience },
        { id: 'education', exists: !!hasEducation },
        { id: 'certifications', exists: !!hasCertifications },
        { id: 'projects', exists: !!hasProjects },
        { id: 'languages', exists: !!hasLanguages },
        { id: 'hobbies', exists: !!hasHobbies },
      ];
  const existingSections = unifiedSections.filter(s => s.exists);

  return (
    <div className="w-full">
      {/* Header with back button and prev/next navigation */}
      {!hideHeader && (
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-800">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          {allTalent.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => prevTalent && onNavigateTalent?.(prevTalent)}
                disabled={!prevTalent || loadingMore}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 disabled:opacity-40 disabled:pointer-events-none rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <button
                onClick={async () => {
                  if (nextTalent) {
                    onNavigateTalent?.(nextTalent);
                  } else if (atEndOfLoadedList && onLoadMore) {
                    setPendingNavigation(true);
                    await onLoadMore();
                  }
                }}
                disabled={!hasNext || loadingMore}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 disabled:opacity-40 disabled:pointer-events-none rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                {loadingMore ? (
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>Next <ChevronRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/*  INFO CARD                                                    */}
      {/* ============================================================ */}
      <div data-section-id="info" className="bg-white dark:bg-slate-900 rounded-xl border border-zinc-200 dark:border-slate-800 p-4 md:p-5 mb-4 relative">
        {/* Viewed badge - top right of info card */}
        {viewedAt && !skipViewTracking && (
          <div className="absolute top-4 right-4 md:top-5 md:right-5">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-100 dark:bg-slate-800 text-zinc-500 dark:text-slate-400" style={{ fontSize: '11px', fontWeight: 500 }}>
              <Eye className="w-3 h-3" aria-hidden />
              Viewed {formatRelativeTime(viewedAt)}
            </span>
          </div>
        )}

        {/* Row 1: Name + verified + yrs badge */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center mb-2 gap-2 flex-wrap">
              <h1 className="text-xl font-semibold text-zinc-900 dark:text-slate-100" style={{ letterSpacing: '-0.01em' }}>
                {developer.name || 'Unknown Talent'}
              </h1>
              {/* Verified badge */}
              <span className="group/verified relative inline-flex items-center flex-shrink-0">
                <span className="cursor-pointer transition-transform duration-150 ease-out hover:scale-110 active:scale-95">
                  <svg viewBox="0 0 22 22" className="w-4 h-4 transition-all duration-150" aria-label="Verified">
                    <g>
                      <circle cx="11" cy="11" r="11" fill="#1D9BF0" className="group-hover/verified:fill-[#1A8CD8]" />
                      <path d="M9.64 15.67L5.47 11.5l1.42-1.42 2.75 2.75 5.67-5.66 1.42 1.41-7.09 7.09z" fill="white" />
                    </g>
                  </svg>
                </span>
                <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2 py-1 text-[11px] font-medium text-white bg-zinc-900 dark:bg-slate-700 rounded-md opacity-0 invisible group-hover/verified:opacity-100 group-hover/verified:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50 shadow-lg">
                  Verified profile
                  <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-zinc-900 dark:border-t-slate-700" />
                </span>
              </span>
              {getDomainConfig(domain).showExperience && developer.yearOfExperience !== null && developer.yearOfExperience !== undefined && (
                <span className="text-sm font-semibold text-zinc-700 dark:text-slate-300 bg-zinc-100 dark:bg-slate-800 px-2 py-1 rounded">
                  {developer.yearOfExperience} yrs
                </span>
              )}
            </div>

            {/* Row 2: Rate · Title · Location (compact) */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2 text-zinc-500 dark:text-slate-400 text-sm mt-0.5">
              {typeof (developer as any).hourlyRate === 'number' && (developer as any).hourlyRate > 0 && (
                <span className="inline-flex items-center font-semibold text-zinc-900 dark:text-slate-100 bg-zinc-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                  ${(developer as any).hourlyRate}/hr
                </span>
              )}
              {developer.title && (
                <div className="flex items-center gap-1">
                  <span>{developer.title}</span>
                </div>
              )}
              {developer.title && developer.country && (
                <span className="hidden md:inline text-zinc-300 dark:text-slate-600">&middot;</span>
              )}
              {developer.country && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span>{developer.country}</span>
                </div>
              )}
            </div>
          </div>

          {/* Save/Discard buttons — hidden in popup (navbar has them), shown on full page */}
          {!hideHeader && (
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              <button
                onClick={isSaved ? handleUnsave : handleSave}
                disabled={actionLoading !== null}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isSaved
                    ? 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-white dark:hover:bg-slate-800'
                    : 'bg-white dark:bg-slate-800 border border-zinc-300 dark:border-slate-600 text-zinc-700 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400'
                }`}
              >
                {(actionLoading === 'save' || actionLoading === 'unsave') ? (
                  <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Heart className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                )}
                {isSaved ? 'Unsave' : 'Save'}
              </button>
              <button
                onClick={isDiscarded ? handleRestore : handleDiscard}
                disabled={actionLoading !== null}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isDiscarded
                    ? 'bg-zinc-200 dark:bg-slate-700 border border-zinc-400 dark:border-slate-500 text-zinc-700 dark:text-slate-300'
                    : 'bg-white dark:bg-slate-800 border border-zinc-300 dark:border-slate-600 text-zinc-700 dark:text-slate-300 hover:bg-zinc-100 dark:hover:bg-slate-700'
                }`}
              >
                {(actionLoading === 'discard' || actionLoading === 'restore') ? (
                  <span className="inline-block w-4 h-4 border-[2.5px] border-zinc-600 dark:border-slate-300 border-t-transparent rounded-full animate-spin" />
                ) : isDiscarded ? (
                  <RotateCcw className="w-4 h-4" />
                ) : (
                  <Ban className="w-4 h-4" />
                )}
                {isDiscarded ? 'Restore' : 'Discard'}
              </button>
            </div>
          )}
        </div>

        {/* Row 3: Company & school logos + Top Performer (inline, consistent with dev card) */}
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm">
          {notableCompanies.slice(0, 2).map((company) => (
            <div key={company} className="flex items-center gap-1">
              <CompanyLogo company={company} size="xs" />
              <span className="text-zinc-600 dark:text-slate-400">{company}</span>
            </div>
          ))}
          {highlightSchools.map((school) => (
            <div key={school.name} className="flex items-center gap-1">
              <UniversityLogo university={school.name} size="xs" />
              <span className="text-zinc-600 dark:text-slate-400">{school.name}</span>
            </div>
          ))}
          {searchTalent?.topPerformer && (
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-500 font-medium">
              <Trophy className="w-3.5 h-3.5" />
              <span>Top Performer</span>
            </span>
          )}
          {searchTalent?.isCFA && (
            <span className="inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-500 font-medium">
              <Award className="w-3.5 h-3.5" />
              <span>CFA</span>
            </span>
          )}
        </div>

        {/* Row 4: Scholar metrics (STEM) or GitHub metrics (SWE) — inline below logos */}
        {(hasDetailScholarMetrics || hasDetailGithubMetrics) && (
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2 text-sm">
            {/* Scholar metrics */}
            {detailScholarCitations != null && (
              <span>
                <span className="font-semibold text-zinc-900 dark:text-slate-100">{formatCount(detailScholarCitations)}</span>
                {' '}<span className="text-zinc-500">citations</span>
              </span>
            )}
            {detailScholarHIndex != null && (
              <span>
                <span className="text-zinc-500">h-index</span>
                {' '}<span className="font-semibold text-zinc-900 dark:text-slate-100">{detailScholarHIndex}</span>
              </span>
            )}
            {hasPublications && publications && (
              <span>
                <span className="font-semibold text-zinc-900 dark:text-slate-100">{publications.length}</span>
                {' '}<span className="text-zinc-500">publications</span>
              </span>
            )}
            {/* GitHub metrics */}
            {detailGithubData?.contributions != null && detailGithubData.contributions > 0 && (
              <span className="inline-flex items-center gap-1">
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-zinc-600 dark:fill-slate-300 flex-shrink-0" aria-hidden="true">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                <span className="font-semibold text-zinc-900 dark:text-slate-100">{formatCount(detailGithubData.contributions)}</span>
                <span className="text-zinc-500">contributions</span>
              </span>
            )}
            {detailGithubData?.repos != null && detailGithubData.repos > 0 && (
              <span>
                <span className="font-semibold text-zinc-900 dark:text-slate-100">{formatCount(detailGithubData.repos)}</span>
                {' '}<span className="text-zinc-500">repos</span>
              </span>
            )}
            {detailGithubData?.stars != null && detailGithubData.stars > 0 && (
              <span>
                <span className="font-semibold text-zinc-900 dark:text-slate-100">{formatCount(detailGithubData.stars)}</span>
                {' '}<span className="text-zinc-500">stars</span>
              </span>
            )}
          </div>
        )}

        {/* Expertise and Skills */}
        {(developer.typeOfWork || (developer.tSkills && developer.tSkills.length > 0) || (developer.skills && developer.skills.length > 0)) && (
          <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-slate-800">
            {developer.typeOfWork && (
              <div className="flex items-start gap-3">
                <span className="text-sm font-medium text-zinc-500 dark:text-slate-400 w-20 flex-shrink-0 pt-0.5">Expertise</span>
                <div className="flex flex-wrap gap-1.5 flex-1">
                  <span className="inline-flex items-center h-[24px] px-2 rounded bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 text-sm font-medium">
                    {developer.typeOfWork}
                  </span>
                </div>
              </div>
            )}
            {((developer.tSkills && developer.tSkills.length > 0) ||
              (developer.skills && developer.skills.length > 0)) && (() => {
              const tSkillNames = (developer.tSkills || [])
                .map(s => (s || '').trim())
                .filter(Boolean);
              const llmSet = new Set(tSkillNames.map(s => s.toLowerCase()));
              const seen = new Set<string>();
              const extraSkills = (developer.skills || [])
                .map(s => (typeof s === 'string' ? s : s?.name || '').trim())
                .filter(name => {
                  if (!name) return false;
                  const k = name.toLowerCase();
                  if (llmSet.has(k) || seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
              const allSkills = [...tSkillNames, ...extraSkills];
              const visibleSkills = showMoreSkills
                ? allSkills
                : allSkills.slice(0, MAX_SKILLS_DISPLAY);
              const hiddenCount = allSkills.length - visibleSkills.length;
              const pillClass = 'inline-flex items-center h-[24px] px-2 rounded bg-zinc-50 dark:bg-slate-800/30 text-zinc-600 dark:text-slate-400 text-sm font-medium';
              return (
                <div className={`flex items-start gap-3 ${developer.typeOfWork ? 'mt-3' : ''}`}>
                  <span className="text-sm font-medium text-zinc-500 dark:text-slate-400 w-20 flex-shrink-0 pt-0.5">Skills</span>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {visibleSkills.map((skill, idx) => (
                      <span key={`${skill}-${idx}`} className={pillClass}>{skill}</span>
                    ))}
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowMoreSkills(true)}
                        className={`${pillClass} border border-dashed border-zinc-400 dark:border-slate-500 cursor-pointer hover:bg-zinc-100 dark:hover:bg-slate-700 transition-colors`}
                      >
                        +{hiddenCount} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {developer.lastContactDate && formatRelativeTimeVerbose(developer.lastContactDate) && (
          <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-slate-800 flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-zinc-500 dark:text-zinc-500" aria-hidden />
            <span className="text-xs text-zinc-500 dark:text-zinc-500">last active {formatRelativeTimeVerbose(developer.lastContactDate)}</span>
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/*  HIGHLIGHTS CARD (always visible, not in unified card)        */}
      {/* ============================================================ */}
      {developer.bayAreaTalentSignals?.proofPoints && developer.bayAreaTalentSignals.proofPoints.length > 0 && (
        <div data-section-id="highlights" className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950/40 dark:to-sky-950/30 rounded-xl border border-blue-200 dark:border-blue-900/50 p-4 md:p-5 mb-4">
          <h2 className="text-lg font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            Highlights
          </h2>
          <ul className="space-y-2">
            {prepareHighlights(developer.bayAreaTalentSignals.proofPoints).map((item, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400 flex-shrink-0 mt-1.5" />
                <span className="text-sm text-blue-900 dark:text-blue-100">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ============================================================ */}
      {/*  UNIFIED DETAILS CARD – all remaining sections in one card    */}
      {/* ============================================================ */}
      {existingSections.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 mb-4">

          {/* ---- Vetting ---- */}
          {hasVetting && (
            <div data-section-id="vetting" data-section-label={vettingSectionTitle}>
              {collapsibleSections ? (
                <InlineSection
                  title={vettingSectionTitle}
                  icon={<Award className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('vetting')}
                  onToggle={() => toggleSection('vetting')}
                  isLast={existingSections[existingSections.length - 1].id === 'vetting'}
                >
                  <VettingContent
                    codingChallenge={codingChallenge}
                    codingChallengeTakeaway={developer.codingChallengeTakeaway}
                    hasAiInterviewContent={hasAiInterviewContent}
                    aiInterview={aiInterview}
                    developer={developer}
                    codingChallengeRecordingLoading={codingChallengeRecordingLoading}
                    codingChallengeRecordingError={codingChallengeRecordingError}
                    codingChallengeRecordingUrl={codingChallengeRecordingUrl}
                    fetchCodingChallengeRecording={fetchCodingChallengeRecording}
                    aiInterviewStreamUrl={aiInterviewStreamUrl}
                    formatDate={formatDate}
                    linkedInVerification={developer.linkedInVerification}
                    linkedInProfileURL={(developer as any).linkedInProfileURL}
                    additionalVerifications={developer.additionalVerifications}
                  />
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'vetting' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    {vettingSectionTitle}
                  </h2>
                  <VettingContent
                    codingChallenge={codingChallenge}
                    codingChallengeTakeaway={developer.codingChallengeTakeaway}
                    hasAiInterviewContent={hasAiInterviewContent}
                    aiInterview={aiInterview}
                    developer={developer}
                    codingChallengeRecordingLoading={codingChallengeRecordingLoading}
                    codingChallengeRecordingError={codingChallengeRecordingError}
                    codingChallengeRecordingUrl={codingChallengeRecordingUrl}
                    fetchCodingChallengeRecording={fetchCodingChallengeRecording}
                    aiInterviewStreamUrl={aiInterviewStreamUrl}
                    formatDate={formatDate}
                    linkedInVerification={developer.linkedInVerification}
                    linkedInProfileURL={(developer as any).linkedInProfileURL}
                    additionalVerifications={developer.additionalVerifications}
                  />
                </div>
              )}
            </div>
          )}

          {/* ---- Education (STEM) ---- */}
          {(isAcademicDomain(domain)) && hasEducation && developer.education && (
            <div data-section-id="education">
              {collapsibleSections ? (
                <InlineSection
                  title="Education"
                  icon={<GraduationCap className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('education')}
                  onToggle={() => toggleSection('education')}
                  isLast={existingSections[existingSections.length - 1].id === 'education'}
                >
                  <EducationContent education={developer.education} />
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'education' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Education
                  </h2>
                  <EducationContent education={developer.education} />
                </div>
              )}
            </div>
          )}

          {/* ---- Work Experience ---- */}
          {hasExperience && developer.workExperience && (
            <div data-section-id="experience">
              {collapsibleSections ? (
                <InlineSection
                  title="Work Experience"
                  icon={<Briefcase className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('experience')}
                  onToggle={() => toggleSection('experience')}
                  isLast={existingSections[existingSections.length - 1].id === 'experience'}
                >
                  <WorkExperienceTimeline workExperience={developer.workExperience} />
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'experience' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Work Experience
                  </h2>
                  <WorkExperienceTimeline workExperience={developer.workExperience} />
                </div>
              )}
            </div>
          )}

          {/* ---- Publications (STEM) ---- */}
          {hasPublications && publications && (
            <div data-section-id="publications">
              {collapsibleSections ? (
                <InlineSection
                  title="Key Publications"
                  icon={<FileText className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('publications')}
                  onToggle={() => toggleSection('publications')}
                  isLast={existingSections[existingSections.length - 1].id === 'publications'}
                >
                  <div className="space-y-3 px-5 pb-5">
                    {publications.map((pub, index) => (
                      <div
                        key={index}
                        className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-900 dark:text-slate-100 text-sm leading-snug mb-1">
                              {pub.title}
                            </h4>
                            {pub.authors && (
                              <p className="text-sm text-slate-500 dark:text-slate-500 mb-1.5">
                                {pub.authors}
                              </p>
                            )}
                            <div className="flex items-center gap-2 text-xs">
                              {pub.year != null && (
                                <span className="text-slate-500">{pub.year}</span>
                              )}
                              {pub.citations != null && (
                                <>
                                  {pub.year != null && <span className="text-slate-400">&middot;</span>}
                                  <span className="text-slate-500 dark:text-slate-300">
                                    {formatCount(pub.citations)} citations
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          {pub.link && (
                            <a
                              href={pub.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                              onClick={(e) => { e.stopPropagation(); trackEvent('Publication Link Clicked', { title: pub.title, url: pub.link }); }}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'publications' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Key Publications
                  </h2>
                  <div className="space-y-3">
                    {publications.map((pub, index) => (
                      <div
                        key={index}
                        className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-900 dark:text-slate-100 text-sm leading-snug mb-1">
                              {pub.title}
                            </h4>
                            {pub.authors && (
                              <p className="text-sm text-slate-500 dark:text-slate-500 mb-1.5">
                                {pub.authors}
                              </p>
                            )}
                            <div className="flex items-center gap-2 text-xs">
                              {pub.year != null && (
                                <span className="text-slate-500">{pub.year}</span>
                              )}
                              {pub.citations != null && (
                                <>
                                  {pub.year != null && <span className="text-slate-400">&middot;</span>}
                                  <span className="text-slate-500 dark:text-slate-300">
                                    {formatCount(pub.citations)} citations
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          {pub.link && (
                            <a
                              href={pub.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                              onClick={(e) => { e.stopPropagation(); trackEvent('Publication Link Clicked', { title: pub.title, url: pub.link }); }}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- Certifications (STEM) ---- */}
          {(isAcademicDomain(domain)) && hasCertifications && developer.certificates && (
            <div data-section-id="certifications">
              {collapsibleSections ? (
                <InlineSection
                  title="Certifications"
                  icon={<CheckCircle className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('certifications')}
                  onToggle={() => toggleSection('certifications')}
                  isLast={existingSections[existingSections.length - 1].id === 'certifications'}
                >
                  <CertificationsContent certifications={developer.certificates} />
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'certifications' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Certifications
                  </h2>
                  <CertificationsContent certifications={developer.certificates} />
                </div>
              )}
            </div>
          )}

          {/* ---- Education (non-STEM: original position) ---- */}
          {!isAcademicDomain(domain) && hasEducation && developer.education && (
            <div data-section-id="education">
              {collapsibleSections ? (
                <InlineSection
                  title="Education"
                  icon={<GraduationCap className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('education')}
                  onToggle={() => toggleSection('education')}
                  isLast={existingSections[existingSections.length - 1].id === 'education'}
                >
                  <EducationContent education={developer.education} />
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'education' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Education
                  </h2>
                  <EducationContent education={developer.education} />
                </div>
              )}
            </div>
          )}

          {/* ---- Certifications (non-STEM: placed right after Education) ---- */}
          {!isAcademicDomain(domain) && hasCertifications && developer.certificates && (
            <div data-section-id="certifications">
              {collapsibleSections ? (
                <InlineSection
                  title="Certifications"
                  icon={<CheckCircle className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('certifications')}
                  onToggle={() => toggleSection('certifications')}
                  isLast={existingSections[existingSections.length - 1].id === 'certifications'}
                >
                  <CertificationsContent certifications={developer.certificates} />
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'certifications' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Certifications
                  </h2>
                  <CertificationsContent certifications={developer.certificates} />
                </div>
              )}
            </div>
          )}

          {/* ---- Key Projects ---- */}
          {hasProjects && developer.projectDetails && (
            <div data-section-id="projects">
              {collapsibleSections ? (
                <InlineSection
                  title="Key Projects"
                  icon={<Code className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('projects')}
                  onToggle={() => toggleSection('projects')}
                  isLast={existingSections[existingSections.length - 1].id === 'projects'}
                >
                  <div className="space-y-3 px-5 pb-5">
                    {developer.projectDetails.map((project, index) => (
                      <div key={index} className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-slate-900 dark:text-slate-100 text-sm">{project.title}</h4>
                          {project.url && (
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                              onClick={(e) => { e.stopPropagation(); trackEvent('Project Link Clicked', { title: project.title, url: project.url }); }}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        {project.description && (
                          <p className="text-sm text-slate-700 dark:text-slate-300 mb-2 leading-relaxed">{project.description}</p>
                        )}
                        {project.skills && project.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {project.skills.map((skill, sIdx) => (
                              <span key={sIdx} className="px-2 py-0.5 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-sm rounded font-medium">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'projects' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Code className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Key Projects
                  </h2>
                  <div className="space-y-3">
                    {developer.projectDetails.map((project, index) => (
                      <div key={index} className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-slate-900 dark:text-slate-100 text-sm">{project.title}</h4>
                          {project.url && (
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                              onClick={(e) => { e.stopPropagation(); trackEvent('Project Link Clicked', { title: project.title, url: project.url }); }}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        {project.description && (
                          <p className="text-sm text-slate-700 dark:text-slate-300 mb-2 leading-relaxed">{project.description}</p>
                        )}
                        {project.skills && project.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {project.skills.map((skill, sIdx) => (
                              <span key={sIdx} className="px-2 py-0.5 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-sm rounded font-medium">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- Languages ---- */}
          {hasLanguages && developer.languages?.natural && (
            <div data-section-id="languages">
              {collapsibleSections ? (
                <InlineSection
                  title="Languages"
                  icon={<Globe className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('languages')}
                  onToggle={() => toggleSection('languages')}
                  isLast={existingSections[existingSections.length - 1].id === 'languages'}
                >
                  <div className="flex flex-wrap gap-2">
                    {developer.languages.natural.filter(l => l?.trim()).map((lang, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm rounded-md font-medium">{lang.trim()}</span>
                    ))}
                  </div>
                </InlineSection>
              ) : (
                <div className={`p-4 md:p-5 ${existingSections[existingSections.length - 1].id !== 'languages' ? 'border-b border-slate-100 dark:border-slate-800' : ''}`}>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Languages
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {developer.languages.natural.filter(l => l?.trim()).map((lang, idx) => (
                      <span key={idx} className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm rounded-md font-medium">{lang.trim()}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- Hobbies ---- */}
          {hasHobbies && developer.hobbies && (
            <div data-section-id="hobbies">
              {collapsibleSections ? (
                <InlineSection
                  title="Hobbies & Interests"
                  icon={<Heart className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  isOpen={expandedSections.has('hobbies')}
                  onToggle={() => toggleSection('hobbies')}
                  isLast={existingSections[existingSections.length - 1].id === 'hobbies'}
                >
                  <div className="flex flex-wrap gap-2">
                    {developer.hobbies.map((hobby, idx) => (
                      <span key={hobby || idx} className="px-3 py-1.5 rounded-full text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">{hobby || 'Unknown'}</span>
                    ))}
                  </div>
                </InlineSection>
              ) : (
                <div className="p-4 md:p-5">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Hobbies & Interests
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {developer.hobbies.map((hobby, idx) => (
                      <span key={hobby || idx} className="px-3 py-1.5 rounded-full text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">{hobby || 'Unknown'}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI disclaimer */}
          <div className="mb-4 p-4 md:p-5">
            <div className="flex items-start gap-2.5">
              <Info className="w-4 h-4 text-gray-400 dark:text-zinc-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-xs text-gray-400 dark:text-zinc-500 leading-relaxed">
                This profile was processed using automated tools and AI. Content may have been summarized or restructured from original sources. Please verify candidate details through your standard hiring process.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Vetting Content (extracted to avoid duplication)                    */
/* ------------------------------------------------------------------ */
function VettingContent({
  codingChallenge,
  codingChallengeTakeaway,
  hasAiInterviewContent,
  aiInterview,
  developer,
  codingChallengeRecordingLoading,
  codingChallengeRecordingError,
  codingChallengeRecordingUrl,
  fetchCodingChallengeRecording,
  aiInterviewStreamUrl,
  formatDate,
  linkedInVerification,
  linkedInProfileURL,
  additionalVerifications,
}: {
  codingChallenge: DeveloperDetails['codingChallengeDetails'];
  codingChallengeTakeaway: DeveloperDetails['codingChallengeTakeaway'];
  hasAiInterviewContent: boolean | null | undefined;
  aiInterview: DeveloperDetails['aiInterviewDetails'];
  developer: DeveloperDetails;
  codingChallengeRecordingLoading: boolean;
  codingChallengeRecordingError: string | null;
  codingChallengeRecordingUrl: string | null;
  fetchCodingChallengeRecording: (submitId: number) => void;
  aiInterviewStreamUrl: string | null;
  formatDate: (dateString: string | null | undefined) => string;
  linkedInVerification?: DeveloperDetails['linkedInVerification'];
  linkedInProfileURL?: string | null;
  additionalVerifications?: DeveloperDetails['additionalVerifications'];
}) {
  // Interview recording may be absent from this environment's bucket (non-prod
  // envs don't hold prod recordings), or a stored URL may be stale. Show a clear
  // message instead of a broken player when the stream fails to load.
  const [aiRecordingError, setAiRecordingError] = useState(false);

  const hasDetailedVerification = !!(linkedInVerification && (linkedInVerification.Identity?.length || linkedInVerification.Workplace?.length || linkedInVerification.EducationalInstitution?.length || linkedInVerification.joinedDate));

  const getYearsAgo = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const years = Math.floor((Date.now() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    return years >= 1 ? `${years}+ years ago` : 'Less than 1 year ago';
  };

  const showLinkedIn = hasDetailedVerification || developer.linkedInVerify === 1;

  const scholarUrl = developer.scholarVerify?.url || (developer as any).googleScholarURL || null;
  const teachingLink = (developer.teachingOrResearchProfile?.isVerified && developer.teachingOrResearchProfile?.link && !developer.teachingOrResearchProfile?.hidden) ? developer.teachingOrResearchProfile.link : null;
  const showResearchCard = !!(scholarUrl || teachingLink);

  const linkedInCard = showLinkedIn ? (
    <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-[#0A66C2] flex items-center justify-center flex-shrink-0">
            <LinkedInLogo className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">LinkedIn</span>
        </div>
        {linkedInProfileURL && (
          <a
            href={linkedInProfileURL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            title="View LinkedIn profile"
            onClick={() => trackEvent('Verification Link Clicked', { section: 'linkedin', url: linkedInProfileURL })}
          >
            Open
            <ExternalLink className="w-3 h-3" aria-hidden />
          </a>
        )}
      </div>
      <div className="space-y-2">
        {hasDetailedVerification && linkedInVerification ? (
          <>
            {linkedInVerification.Identity?.map((item, i) => (
              <div key={`id-${i}`} className="flex items-start gap-2">
                <BlueTick className="w-4 h-4 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Identity Verified</span>
                    {item.Date && <span className="text-sm text-slate-400 dark:text-slate-500 ml-1">· {item.Date.replace(/^Over\s+/i, '')}</span>}
                  </div>
                  {item.Value && <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{item.Value}</div>}
                </div>
              </div>
            ))}
            {linkedInVerification.Workplace?.map((item, i) => (
              <div key={`wp-${i}`} className="flex items-start gap-2">
                <BlueTick className="w-4 h-4 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Workplace Verified</span>
                    {item.Date && <span className="text-sm text-slate-400 dark:text-slate-500 ml-1">· {item.Date.replace(/^Over\s+/i, '')}</span>}
                  </div>
                  {item.Value && <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{item.Value}</div>}
                </div>
              </div>
            ))}
            {linkedInVerification.EducationalInstitution?.map((item, i) => (
              <div key={`edu-${i}`} className="flex items-start gap-2">
                <BlueTick className="w-4 h-4 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Education Verified</span>
                    {item.Date && <span className="text-sm text-slate-400 dark:text-slate-500 ml-1">· {item.Date.replace(/^Over\s+/i, '')}</span>}
                  </div>
                  {item.Value && <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{item.Value}</div>}
                </div>
              </div>
            ))}
            {linkedInVerification.joinedDate && (
              <div className="flex items-start gap-2">
                <BlueTick className="w-4 h-4 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Created</span>
                    <span className="text-sm text-slate-400 dark:text-slate-500 ml-1">· {getYearsAgo(linkedInVerification.joinedDate)}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <BlueTick className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Location</span>
            </div>
            <div className="flex items-center gap-2">
              <BlueTick className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Work history</span>
            </div>
            <div className="flex items-center gap-2">
              <BlueTick className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Education</span>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  const researchCard = showResearchCard ? (
    <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
      <div className="flex items-center mb-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-zinc-900 dark:bg-zinc-600 flex items-center justify-center flex-shrink-0">
            <Code className="w-4 h-4 text-white" aria-hidden />
          </div>
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Work Artifacts</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {scholarUrl && (
          <div className="flex items-center gap-2">
            <BlueTick className="w-4 h-4 flex-shrink-0" />
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <GoogleScholarLogo className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Google Scholar verified</span>
            </div>
            <a
              href={scholarUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex-shrink-0"
              title="View Google Scholar profile"
              onClick={() => trackEvent('Verification Link Clicked', { section: 'google_scholar', url: scholarUrl })}
            >
              Open
              <ExternalLink className="w-3 h-3" aria-hidden />
            </a>
          </div>
        )}
        {teachingLink && (() => {
          let hostname = '';
          try { hostname = new URL(teachingLink).hostname.replace(/^www\./, ''); } catch { /* ignore */ }
          return (
            <div className="flex items-center gap-2">
              <BlueTick className="w-4 h-4 flex-shrink-0" />
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                {hostname && (
                  <CompanyLogo company={hostname} size="xs" />
                )}
                <span className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">Teaching/Research Page</span>
              </div>
              <a
                href={teachingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex-shrink-0"
                title="View university profile"
                onClick={() => trackEvent('Verification Link Clicked', { section: 'teaching_research', url: teachingLink })}
              >
                Open
                <ExternalLink className="w-3 h-3" aria-hidden />
              </a>
            </div>
          );
        })()}
      </div>
    </div>
  ) : null;

  const showGrid = showLinkedIn || showResearchCard;
  const isOpenableLink = (link?: string | null) => {
    const trimmed = link?.trim();
    if (!trimmed) return false;
    try { return /^https?:$/.test(new URL(trimmed).protocol); } catch { return false; }
  };
  const filteredAdditionalVerifs = (additionalVerifications?.filter(item => isOpenableLink(item.link)) ?? [])
    .filter(item => !teachingLink || item.link!.trim() !== teachingLink);
  const hasAdditionalVerifs = filteredAdditionalVerifs.length > 0;
  const showVerificationsBlock = showGrid || hasAdditionalVerifs;

  return (
    <div className="space-y-3">
      {/* Coding Challenge */}
      {codingChallenge && codingChallenge.submitId && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">Coding Challenge</h4>
                {codingChallenge.programmingLanguage?.trim() && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium">
                    <Code className="w-3.5 h-3.5 opacity-70" aria-hidden />
                    {codingChallenge.programmingLanguage.trim()}
                  </span>
                )}
              </div>
            </div>
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <details
            className="mb-3 group"
            onToggle={(e) => {
              if ((e.target as HTMLDetailsElement).open && codingChallenge.submitId) {
                fetchCodingChallengeRecording(codingChallenge.submitId);
              }
            }}
          >
            <summary className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium cursor-pointer list-none">
              <Play className="w-4 h-4 group-open:hidden" />
              <ChevronDown className="w-5 h-5 hidden group-open:block" />
              <span>Watch Challenge Recording</span>
            </summary>
            <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-3">
              {codingChallengeRecordingLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              )}
              {codingChallengeRecordingError && (
                <div className="text-center py-8"><p className="text-red-600 dark:text-red-400">{codingChallengeRecordingError}</p></div>
              )}
              {codingChallengeRecordingUrl && (
                <iframe title="Coding Challenge Recording" src={codingChallengeRecordingUrl} frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full aspect-video rounded-md" />
              )}
            </div>
          </details>
          {false && codingChallenge?.tSummaryAndPerformance && (
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{codingChallenge?.tSummaryAndPerformance}</p>
          )}
        </div>
      )}

      {/* AI Interview */}
      {hasAiInterviewContent && aiInterview && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-sm mb-1">Work Experience Deep Dive</h4>
              {aiInterview.date && (
                <p className="text-sm text-slate-500 dark:text-slate-400">Completed {formatDate(aiInterview.date)}</p>
              )}
            </div>
          </div>
          {aiInterviewStreamUrl && (
            <details className="mb-3 group">
              <summary className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium cursor-pointer list-none">
                <Play className="w-4 h-4 group-open:hidden" />
                <ChevronDown className="w-5 h-5 hidden group-open:block" />
                <span>Watch Interview Recording</span>
              </summary>
              <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-3">
                {aiRecordingError ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-4 justify-center text-center">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>Interview recording is currently unavailable.</span>
                  </div>
                ) : (
                  <video
                    src={aiInterviewStreamUrl}
                    controls
                    controlsList="nodownload noremoteplayback"
                    preload="metadata"
                    playsInline
                    className="w-full rounded-md"
                    onError={() => setAiRecordingError(true)}
                  />
                )}
              </div>
            </details>
          )}
          {aiInterview.takeaways && Array.isArray(aiInterview.takeaways) && aiInterview.takeaways.length > 0 && (
            <div className="bg-slate-50 dark:bg-slate-800 rounded p-3 mb-3">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Key Takeaways</p>
              <ul className="space-y-1">
                {aiInterview.takeaways.map((item, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {aiInterview.transcript && Array.isArray(aiInterview.transcript) && aiInterview.transcript.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 font-medium">View Transcript</summary>
              <div className="mt-3 bg-slate-50 dark:bg-slate-800 rounded p-3 text-slate-700 dark:text-slate-300 text-sm leading-relaxed space-y-3">
                {aiInterview.transcript.map((segment, i) => (
                  <p key={i} className="m-0">
                    <span className="font-medium">{segment.id === 'interviewer' ? 'Interviewer' : 'Response'}:</span>{' '}
                    {segment.content}
                  </p>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Verifications block */}
      {showVerificationsBlock && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-4 h-4" aria-hidden />
              </div>
              <h4 className="font-medium text-slate-900 dark:text-slate-100 text-md leading-tight">Verifications</h4>
            </div>
          </div>

          {showGrid && (
            <div className={`grid grid-cols-1 ${showLinkedIn && showResearchCard ? 'md:grid-cols-2' : ''} gap-3`}>
              {linkedInCard}
              {researchCard}
            </div>
          )}

          {hasAdditionalVerifs && (
            <div className={`border border-slate-200 dark:border-slate-700 rounded-lg p-3${showGrid ? ' mt-3' : ''}`}>
              <div className="flex items-center gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 dark:bg-slate-900/30">
                    <FileText className="w-4 h-4 text-slate-600 dark:text-slate-400" aria-hidden />
                  </div>
                  <h5 className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-tight">Additional Verifications</h5>
                </div>
              </div>
              <ul className="mt-3 space-y-1.5">
                {filteredAdditionalVerifs.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <BlueTick className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {item.text ? (
                      <span className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed flex-1">{item.text.charAt(0).toUpperCase() + item.text.slice(1)}</span>
                    ) : item.link ? (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed flex-1">{item.link}</a>
                    ) : null}
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex-shrink-0 mt-0.5"
                        onClick={() => trackEvent('Verification Link Clicked', { section: 'additional_verification', label: item.text || '', url: item.link })}
                      >
                        Open
                        <ExternalLink className="w-3 h-3" aria-hidden />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-3 text-xs text-slate-500 dark:text-slate-500 leading-relaxed">
            <Info className="w-3 h-3 inline-block mr-1 -mt-0.5" aria-hidden />
            Note: All talent undergoes identity verification, SSN validation where applicable, tax and payment setup, liveness (video selfie) checks, and financial crime screening before onboarding.
          </p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Certifications Content                                             */
/* ------------------------------------------------------------------ */
function CertificationsContent({ certifications }: { certifications: NonNullable<DeveloperDetails['certificates']> }) {
  return (
    <div className="space-y-1">
      {certifications.map((cert, idx) => {
        const name = cert.name?.trim() || null;
        const org = cert.certOrg?.trim() || null;
        const year = cert.year != null ? cert.year : null;
        if (!name) return null;
        return (
          <div key={idx} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-slate-900 dark:text-slate-100 text-sm">{name}</h4>
              {org && <p className="text-xs text-slate-500 dark:text-slate-400">{org}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {year && <span className="text-sm text-slate-500 dark:text-slate-400">{year}</span>}
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Education Content with university logos                             */
/* ------------------------------------------------------------------ */
function EducationContent({ education }: { education: NonNullable<DeveloperDetails['education']> }) {
  return (
    <div className="space-y-4">
      {education.map((edu, idx) => {
        const level = edu.level?.trim() || null;
        const degree = edu.degree?.trim() || null;
        const school = edu.school?.trim() || null;
        const startYear = edu.startYear != null ? edu.startYear : null;
        const endYear = edu.endYear != null ? edu.endYear : null;
        const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
        const degreeLabel = (level && degree) ? `${cap(level)}, ${degree}` : level ? cap(level) : degree ?? null;
        const dateRange = (() => {
          if (startYear != null && endYear != null) return `${startYear} - ${endYear}`;
          if (endYear != null) return String(endYear);
          if (startYear != null) return `${startYear} - Present`;
          return null;
        })();
        const subtitleParts = [degreeLabel, dateRange].filter(Boolean);
        if (!school && subtitleParts.length === 0) return null;
        return (
          <div key={idx} className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <UniversityLogo university={school || 'Unknown'} size="md" />
            </div>
            <div className="flex-1 min-w-0">
              {school && <h4 className="font-medium text-slate-900 dark:text-slate-100 text-sm">{school}</h4>}
              {subtitleParts.length > 0 && (
                <p className="text-sm text-slate-600 dark:text-slate-400">{subtitleParts.join(' · ')}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default DeveloperProfile;
